import { Pill, EyeOff, AlertCircle, Ban, Scale, Info, Phone, MessageSquare } from "lucide-react";
import { Reveal, Eyebrow } from "./primitives";
import { REPRO } from "@/lib/content";

const SIGN_ICONS = [Pill, EyeOff, AlertCircle, Ban];

export default function ReproCoercion() {
  return (
    <section id="coercion" data-testid="repro-section" className="bg-bone py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* Definition */}
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>{REPRO.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
              {REPRO.heading}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-stone sm:text-lg">{REPRO.lead}</p>
          </Reveal>
        </div>

        {/* Signs */}
        <div className="mt-14">
          <Reveal>
            <h3 className="font-serif text-2xl italic text-sage">Common signs & examples</h3>
          </Reveal>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {REPRO.signs.map((s, i) => {
              const Icon = SIGN_ICONS[i % SIGN_ICONS.length];
              return (
                <Reveal key={s.title} delay={(i % 4) * 0.06} data-testid={`repro-sign-${i}`}>
                  <div className="flex h-full flex-col rounded-3xl bg-sand p-7 transition-transform duration-500 hover:-translate-y-1">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-terracotta/15 text-terracotta">
                      <Icon className="h-5 w-5" strokeWidth={2.2} />
                    </span>
                    <h4 className="mt-5 font-serif text-xl font-medium text-ink">{s.title}</h4>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-stone">{s.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Effects + Hotline */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Reveal className="rounded-3xl border border-line bg-sand p-8 sm:p-10">
            <h3 className="font-serif text-2xl italic text-sage">Risks & effects</h3>
            <ul className="mt-5 space-y-4">
              {REPRO.effects.map((e, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`repro-effect-${i}`}>
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" />
                  <p className="text-base leading-relaxed text-stone">{e}</p>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.08} className="rounded-3xl bg-ink p-8 text-bone sm:p-10" data-testid="repro-hotline">
            <p className="text-base leading-relaxed text-bone/80 sm:text-lg">{REPRO.hotline.text}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={REPRO.hotline.callHref}
                data-testid="repro-hotline-call"
                className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105"
              >
                <Phone className="h-4 w-4" strokeWidth={2.4} />
                {REPRO.hotline.call}
              </a>
              <a
                href={REPRO.hotline.textHref}
                data-testid="repro-hotline-text"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-bone transition-colors duration-300 hover:bg-white/10"
              >
                <MessageSquare className="h-4 w-4" strokeWidth={2.2} />
                {REPRO.hotline.textLine}
              </a>
            </div>
          </Reveal>
        </div>

        {/* Legal recognition */}
        <Reveal delay={0.1} className="mt-8 rounded-3xl border border-line bg-sand p-8 sm:p-10" data-testid="repro-legal">
          <div className="flex items-center gap-3">
            <Scale className="h-6 w-6 text-terracotta" strokeWidth={2} />
            <span className="text-sm font-bold uppercase tracking-[0.18em] text-sage">
              {REPRO.legalLabel}
            </span>
          </div>
          <p className="mt-5 max-w-4xl text-base leading-relaxed text-stone sm:text-lg">{REPRO.legal}</p>
        </Reveal>

        <Reveal delay={0.14} className="mt-6 flex items-start gap-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={2.2} />
          <p className="text-xs leading-relaxed text-stone">{REPRO.disclaimer}</p>
        </Reveal>
      </div>
    </section>
  );
}
